/* eslint-disable no-unused-vars */
import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  CSSProperties,
} from "react";
// libraries
import classNames from "classnames";

// styles
import styles from "./styles.module.scss";
import { normalizeValue } from "../../../setup/util/helpers";

export enum InputType {
  DEFAULT = "DEFAULT",
  NUMBER = "NUMBER",
  PHONE_NUMBER = "PHONE_NUMBER",
  DATE = "DATE",
  PASSWORD = "PASSWORD",
}

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  defaultValue?: string | number;
  disabledValue?: string | number;
  maxCharacters?: number;
  maxDecimals?: number;
  maxValue?: number;
  // eslint-disable-next-line no-unused-vars
  handleChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: InputType;
  required?: boolean;
  description?: string;
  hint?: string;
  errorMessage?: string;
  preventMaxValuePass?: boolean;
  containerWidth?: string;
  containerHeight?: string;
  outerContainerWidth?: string;
  placeholder?: string;
  isSettings?: boolean;
  disabled?: boolean;
  style?: CSSProperties;
}

const TextInput: React.FC<Props> = ({
  label = "",
  defaultValue = "",
  disabledValue = "",
  maxCharacters = 100,
  maxDecimals = 2,
  maxValue,
  handleChange,
  required,
  hint,
  type = InputType.DEFAULT,
  description = "",
  errorMessage,
  preventMaxValuePass = false,
  containerWidth = "auto",
  containerHeight,
  outerContainerWidth,
  placeholder,
  isSettings,
  disabled,
  style,
  ...props
}: Props) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const prevPropsValueRef = useRef<
    string | number | readonly string[] | undefined
  >(props.value);
  const isFirstMountRef = useRef<boolean>(true);
  const prevDisabledValueRef = useRef<string | number | undefined>(
    disabledValue
  );

  const [value, setValue] = useState<string | number>(
    normalizeValue(props.value ?? defaultValue)
  );
  const [error, setError] = useState<boolean>(false);
  const [focused, setFocused] = useState<boolean>(false);

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (
        preventMaxValuePass &&
        maxValue &&
        Number(e.target.value) > maxValue
      ) {
        return;
      }
      setError(false);
      setValue(e.target.value);
      handleChange?.(e);
    },
    [handleChange, preventMaxValuePass, maxValue]
  );

  useEffect(() => {
    const wasDefined =
      prevDisabledValueRef.current != null &&
      prevDisabledValueRef.current !== "";
    const isNowUndefined = disabledValue == null || disabledValue === "";

    if (disabledValue != null && disabledValue !== "") {
      setValue(disabledValue || "");
    } else if (wasDefined && isNowUndefined) {
      setValue("");
    }

    prevDisabledValueRef.current = disabledValue;
  }, [disabledValue]);

  // Sync with external value changes (only when props.value changes externally, not during user input)
  useEffect(() => {
    const propsValueChanged = prevPropsValueRef.current !== props.value;
    const isFirstMount = isFirstMountRef.current;

    if (propsValueChanged || isFirstMount) {
      prevPropsValueRef.current = props.value;

      if (focused && !isFirstMount) {
        isFirstMountRef.current = false;
        return;
      }

      if (isSettings) {
        const normalizedDefault = normalizeValue(defaultValue);
        setValue(normalizedDefault || "");
      } else if (props.value != null && props.value !== "") {
        const normalized = normalizeValue(props.value);
        setValue(normalized);
      } else if (defaultValue != null && defaultValue !== "") {
        const normalizedDefault = normalizeValue(defaultValue);
        setValue(normalizedDefault);
      } else {
        setValue("");
      }
      isFirstMountRef.current = false;
    }
  }, [defaultValue, props.value, isSettings, focused]);

  useEffect(() => {
    const value = inputRef?.current?.value;

    if (focused && ((required && !value) || !!errorMessage)) {
      setError(true);
    } else {
      setError(false);
    }
    if (maxValue && Number(value) > maxValue) {
      setError(true);
    }
  }, [
    focused,
    maxValue,
    required,
    value,
    inputRef?.current?.value,
    errorMessage,
  ]);

  const validateNumber = (
    value: React.KeyboardEvent<HTMLTextAreaElement>["key"]
  ) => {
    if (/[0-9.]/.test(value)) return true;
    return false;
  };

  const errorClass = error ? styles.error : "";

  return (
    <div
      className={classNames(styles.container_outer)}
      style={{ width: outerContainerWidth ? outerContainerWidth : undefined }}>
      {label && (
        <div className={styles["header"]}>
          <p className={classNames(styles["p1"], styles["g400"])}>{label}</p>
        </div>
      )}
      <div
        className={classNames(styles.container, errorClass)}
        style={{
          width: containerWidth,
          height: containerHeight ? containerHeight : undefined,
        }}>
        <input
          disabled={disabled}
          type={type === InputType.PASSWORD ? "password" : undefined}
          ref={inputRef}
          placeholder={placeholder}
          className={classNames(
            styles.input,
            type === InputType.NUMBER ? styles["number"] : ""
          )}
          onChange={onChange}
          style={{
            width: "100%",
            minHeight: "16px",
            ...style,
          }}
          onFocus={() => {
            setFocused(true);
          }}
          onBlur={() => {
            setFocused(false);
          }}
          onKeyPress={(event) => {
            if (String(value).length >= maxCharacters) event.preventDefault();
            if (
              type === InputType.NUMBER &&
              String(value).includes(".") &&
              String(value).split(".")[1].length >= maxDecimals
            )
              event.preventDefault();
            if (
              type === InputType.NUMBER &&
              ((String(value) === "" && event.key === ".") ||
                (String(value).includes(".") && event.key === "."))
            )
              event.preventDefault();
            if (!validateNumber(event?.key) && type === InputType.NUMBER)
              event.preventDefault();

            if (
              type === InputType.PHONE_NUMBER &&
              (event?.key === " " ||
                event?.key === "e" ||
                (!validateNumber(event?.key) && event?.key !== "+") ||
                event?.key === "-" ||
                event?.key === ".")
            )
              event?.preventDefault();
          }}
          value={value}
          {...props}
        />
        {description && (
          <p className={classNames(styles["p4"], styles["g400"])}>
            {description}
          </p>
        )}
      </div>

      {hint && <div className={styles["hint"]}>{hint}</div>}
      <div className={styles["error_container"]}>
        {errorMessage && (
          <p dir={"rlt"} className={styles["error_text"]}>
            {errorMessage}
          </p>
        )}
      </div>
    </div>
  );
};

export default React.memo(TextInput);
